import { Note } from 'webmidi'
import type { MidiNoteEvent } from '../events/midi-event.js'

/**
 * A note with an ABSOLUTE time, in ms from the moment playback is scheduled.
 *
 * `playSequence` walks note-to-note by duration and has no absolute time, so it cannot express a
 * melody's `offsetMs` - the feel would be quantized away at the one place it matters. This shape
 * is what `AudioEngine.playScheduled` takes, and `MelodyLib.scheduleMelody` produces it.
 */
export interface ScheduledNote {
  midi: number
  /** 0-1 */
  velocity: number
  /** ms from the schedule call. */
  timeMs: number
  durationMs: number
}

/**
 * Transport-relative time strings for one note's on and off.
 *
 * `leadMs` clears the Tone transport's lookahead (`clockUpdateInterval`, 100 ms by default): a
 * callback scheduled inside it fires late, which for feel playback is exactly the error the whole
 * entity exists to avoid. Times are clamped at `+0` - a large negative `offsetMs` must not
 * schedule into the past.
 */
export const toTransportTimes = (note: ScheduledNote, leadMs: number): { onAt: string; offAt: string } => {
  const onMs = Math.max(0, note.timeMs + leadMs)
  return { onAt: `+${onMs / 1000}`, offAt: `+${(onMs + note.durationMs) / 1000}` }
}

export const scheduledNoteToMidi = (note: ScheduledNote): [MidiNoteEvent, MidiNoteEvent] => [
  { type: 'noteon', note: new Note(note.midi), value: note.velocity },
  { type: 'noteoff', note: new Note(note.midi) },
]
